import { useEffect, useRef, useState } from 'react';

interface PreloaderProps {
    onComplete: () => void;
}

export default function Preloader({ onComplete }: PreloaderProps) {
    const [progress, setProgress] = useState(0);
    const [logoVisible, setLogoVisible] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const completedRef = useRef(false);

    useEffect(() => {
        const loaded = document.readyState === 'complete';
        let raf: number;

        // Animate progress: fast to 80% while loading, then jump to 100% on load
        const startTime = Date.now();

        const tick = () => {
            if (completedRef.current) return;
            const elapsed = Date.now() - startTime;

            if (document.readyState === 'complete') {
                // Page loaded — fill to 100%
                setProgress(100);
                finishUp();
                return;
            }

            // Ease toward 85% over ~3s while still loading
            const target = 85;
            const p = Math.min(target, (elapsed / 3000) * target);
            setProgress(p);
            raf = requestAnimationFrame(tick);
        };

        const finishUp = () => {
            if (completedRef.current) return;
            completedRef.current = true;
            // Show logo briefly, then fade out
            setTimeout(() => setLogoVisible(true), 100);
            setTimeout(() => setFadeOut(true), 700);
            setTimeout(() => onComplete(), 1200);
        };

        if (loaded) {
            // Already loaded — show briefly then complete
            setProgress(100);
            finishUp();
        } else {
            raf = requestAnimationFrame(tick);
            window.addEventListener('load', () => {
                setProgress(100);
                finishUp();
            });
        }

        return () => {
            if (raf) cancelAnimationFrame(raf);
        };
    }, [onComplete]);

    return (
        <div
            className="fixed inset-0 z-9999 flex flex-col items-center justify-center bg-white"
            style={{
                opacity: fadeOut ? 0 : 1,
                transition: 'opacity 0.5s ease-out',
                pointerEvents: fadeOut ? 'none' : 'auto',
            }}
        >
            {/* Logo */}
            <div
                style={{
                    opacity: logoVisible ? 1 : 0,
                    transform: logoVisible ? 'scale(1)' : 'scale(0.85)',
                    transition: 'opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
            >
                <img
                    src="/logo/podus_full_1.svg"
                    alt="Podus AI"
                    className="h-10 lg:h-12"
                />
            </div>

            {/* Progress bar */}
            <div className="absolute bottom-16 left-1/2 w-48 -translate-x-1/2">
                {/* Percentage */}
                <p
                    className="mb-4 text-center text-2xl font-bold tracking-tight"
                    style={{
                        opacity: logoVisible ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    <span className="text-secondary">{Math.round(progress)}</span>
                    <span className="text-secondary-100">%</span>
                </p>

                {/* Bar track */}
                <div
                    className="h-[3px] w-full overflow-hidden rounded-full bg-gray-100"
                    style={{
                        opacity: logoVisible ? 0 : 1,
                        transition: 'opacity 0.3s ease',
                    }}
                >
                    <div
                        className="h-full rounded-full bg-secondary"
                        style={{
                            width: `${progress}%`,
                            transition: 'width 0.15s linear',
                        }}
                    />
                </div>
            </div>
        </div>
    );
}
